/**
 * AdminMarketRateHistoryTab — admin overview of all market-rate history snapshots.
 *
 * Displays a paginated, filterable table of every market-rate snapshot recorded
 * across all properties. Each row can be drilled into to show the full history
 * trend for that property on a recharts line chart.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Card, CardContent, CardHeader, CardTitle,
} from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import {
  TrendingUp, Building2, BarChart2, Loader2,
  Search, ChevronLeft, ChevronRight, LineChart as LineChartIcon, X,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as ChartTooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── helpers ────────────────────────────────────────────────────────────────

function fmt(raw) {
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return raw;
  }
}

function fmtDate(raw) {
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return raw;
  }
}

function fmtGBP(val) {
  if (val == null) return '—';
  return `£${Number(val).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function fmtPct(val) {
  if (val == null) return '—';
  return `${Number(val).toFixed(1)}%`;
}

const SOURCE_LABELS = {
  auto: 'Auto',
  manual: 'Manual',
  on_demand: 'On-demand',
};

const TRIGGER_LABELS = {
  monthly_auto_record: 'Monthly job',
  initial_auto_load: 'Initial load',
  landlord_dashboard_refresh: 'Landlord refresh',
  landlord_forced_refresh: 'Forced refresh',
  landlord_refresh: 'Landlord',
};

function sourceBadge(source) {
  if (source === 'auto') return <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-xs">Auto</Badge>;
  if (source === 'manual') return <Badge className="bg-purple-100 text-purple-700 border-purple-200 text-xs">Manual</Badge>;
  return <Badge variant="outline" className="text-xs">{SOURCE_LABELS[source] || source}</Badge>;
}

function pctBadge(pct) {
  if (pct == null) return <span className="text-muted-foreground">—</span>;
  const n = Number(pct);
  if (n < 90) return <Badge className="bg-green-100 text-green-700 border-green-200 text-xs">{n.toFixed(1)}%</Badge>;
  if (n <= 110) return <Badge className="bg-yellow-100 text-yellow-700 border-yellow-200 text-xs">{n.toFixed(1)}%</Badge>;
  return <Badge className="bg-red-100 text-red-700 border-red-200 text-xs">{n.toFixed(1)}%</Badge>;
}

// ── stat card ──────────────────────────────────────────────────────────────

function StatCard({ icon: Icon, label, value, sub, color = 'blue' }) {
  const colors = {
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    green: 'bg-green-50 text-green-600',
  };
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-4">
        <div className={`rounded-lg p-2.5 ${colors[color]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-xl font-bold">{value}</p>
          {sub && <p className="text-xs text-muted-foreground">{sub}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

// ── trend dialog ───────────────────────────────────────────────────────────

function TrendDialog({ open, onClose, propertyId, postcode, bedrooms, bedroomsRequested }) {
  const [loading, setLoading] = useState(false);
  const [points, setPoints] = useState([]);

  useEffect(() => {
    if (!open || !propertyId) return;
    setLoading(true);
    axios
      .get(`${API_URL}/api/landlord/market-rate-history`, {
        params: { property_id: propertyId, limit: 365 },
        withCredentials: true,
      })
      .then((r) => setPoints(r.data.points || []))
      .catch(() => toast.error('Failed to load property trend'))
      .finally(() => setLoading(false));
  }, [open, propertyId]);

  const chartData = points.map((p) => ({
    date: fmtDate(p.recorded_at || p.captured_at),
    rawDate: p.recorded_at || p.captured_at,
    median: p.median_rent != null ? Number(p.median_rent.toFixed(0)) : null,
    min: p.min_rent != null ? Number(p.min_rent.toFixed(0)) : null,
    max: p.max_rent != null ? Number(p.max_rent.toFixed(0)) : null,
    current: p.current_rent != null ? Number(p.current_rent.toFixed(0)) : null,
  }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LineChartIcon className="h-4 w-4 text-muted-foreground" />
            Market Rate History
          </DialogTitle>
          <DialogDescription>
            {postcode || propertyId}{bedrooms != null ? ` · ${bedrooms} bed` : ''}
          {bedroomsRequested != null && bedroomsRequested !== bedrooms ? ` (requested ${bedroomsRequested} bed — fallback used)` : ''}
            {' — '}all recorded snapshots
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!loading && chartData.length === 0 && (
          <div className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
            No history snapshots found for this property.
          </div>
        )}

        {!loading && chartData.length > 0 && (
          <>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tick={{ fontSize: 11 }}
                    tickFormatter={(v) => `£${v}`}
                    tickLine={false}
                    axisLine={false}
                    width={56}
                  />
                  <ChartTooltip
                    formatter={(value, name) => [`£${value?.toLocaleString() ?? '—'}`, name]}
                    labelFormatter={(label) => `Date: ${label}`}
                    contentStyle={{ fontSize: 12, borderRadius: 8 }}
                  />
                  <Legend iconSize={10} wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="median" name="Market Median" stroke="#6366f1" strokeWidth={2} dot={false} />
                  <Line type="monotone" dataKey="current" name="Current Rent" stroke="#10b981" strokeWidth={2} dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="min" name="Min" stroke="#94a3b8" strokeWidth={1} strokeDasharray="4 2" dot={false} />
                  <Line type="monotone" dataKey="max" name="Max" stroke="#f59e0b" strokeWidth={1} strokeDasharray="4 2" dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* snapshot table */}
            <div className="mt-4 max-h-56 overflow-y-auto rounded-lg border text-sm">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-xs">Recorded</TableHead>
                    <TableHead className="text-xs">Trigger</TableHead>
                    <TableHead className="text-xs">Current Rent</TableHead>
                    <TableHead className="text-xs">Median</TableHead>
                    <TableHead className="text-xs">% of Median</TableHead>
                    <TableHead className="text-xs">Listings</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...points].reverse().map((p, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs whitespace-nowrap">{fmtDate(p.recorded_at)}</TableCell>
                      <TableCell className="text-xs">{TRIGGER_LABELS[p.trigger] || p.trigger || '—'}</TableCell>
                      <TableCell className="text-xs">{fmtGBP(p.current_rent)}</TableCell>
                      <TableCell className="text-xs">{fmtGBP(p.median_rent)}</TableCell>
                      <TableCell className="text-xs">{pctBadge(p.current_rent_pct_of_median)}</TableCell>
                      <TableCell className="text-xs">{p.listings_count ?? '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── main tab ───────────────────────────────────────────────────────────────

export function AdminMarketRateHistoryTab({ revision = 0 }) {
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState({});
  const [page, setPage] = useState(1);
  const LIMIT = 50;

  // filters
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [triggerFilter, setTriggerFilter] = useState('all');
  const [fromAt, setFromAt] = useState('');
  const [toAt, setToAt] = useState('');

  // trend dialog
  const [trendProp, setTrendProp] = useState(null); // { property_id, postcode, bedrooms }

  const fetchHistory = useCallback(async (pg = 1) => {
    setLoading(true);
    try {
      const params = { page: pg, limit: LIMIT };
      if (search.trim()) params.postcode = search.trim();
      if (sourceFilter !== 'all') params.source = sourceFilter;
      if (triggerFilter !== 'all') params.trigger = triggerFilter;
      if (fromAt) params.from_at = fromAt;
      if (toAt) params.to_at = toAt;

      const r = await axios.get(`${API_URL}/api/admin/market-rate-history`, {
        params,
        withCredentials: true,
      });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
      setSummary(r.data.summary || {});
      setPage(pg);
    } catch (err) {
      toast.error('Failed to load market rate history');
    } finally {
      setLoading(false);
    }
  }, [search, sourceFilter, triggerFilter, fromAt, toAt]);

  useEffect(() => {
    fetchHistory(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [revision]);

  const handleApply = () => fetchHistory(1);
  const handleClear = () => {
    setSearch('');
    setSourceFilter('all');
    setTriggerFilter('all');
    setFromAt('');
    setToAt('');
    setTimeout(() => fetchHistory(1), 0);
  };

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div className="space-y-5">
      {/* ── stat cards ── */}
      <div className="grid gap-4 sm:grid-cols-3">
        <StatCard
          icon={BarChart2}
          label="Total Snapshots"
          value={loading ? '…' : (summary.total_snapshots ?? 0).toLocaleString()}
          color="blue"
        />
        <StatCard
          icon={Building2}
          label="Properties Tracked"
          value={loading ? '…' : (summary.properties_tracked ?? 0).toLocaleString()}
          color="purple"
        />
        <StatCard
          icon={TrendingUp}
          label="Last Snapshot"
          value={loading ? '…' : (summary.latest_recorded_at ? fmtDate(summary.latest_recorded_at) : '—')}
          sub={summary.latest_recorded_at ? fmt(summary.latest_recorded_at) : undefined}
          color="green"
        />
      </div>

      {/* ── filters + table card ── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Market Rate History</CardTitle>

          {/* filter row */}
          <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6">
            <div className="relative col-span-1 xl:col-span-2">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search postcode…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleApply()}
                className="pl-8 h-8 text-sm"
              />
            </div>

            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="All Sources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="auto">Auto</SelectItem>
                <SelectItem value="manual">Manual</SelectItem>
                <SelectItem value="on_demand">On-demand</SelectItem>
              </SelectContent>
            </Select>

            <Select value={triggerFilter} onValueChange={setTriggerFilter}>
              <SelectTrigger className="h-8 text-sm">
                <SelectValue placeholder="All Triggers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Triggers</SelectItem>
                <SelectItem value="monthly_auto_record">Monthly job</SelectItem>
                <SelectItem value="initial_auto_load">Initial load</SelectItem>
                <SelectItem value="landlord_dashboard_refresh">Landlord refresh</SelectItem>
                <SelectItem value="landlord_forced_refresh">Forced refresh</SelectItem>
              </SelectContent>
            </Select>

            <Input
              type="date"
              value={fromAt}
              onChange={(e) => setFromAt(e.target.value)}
              className="h-8 text-sm"
              title="Recorded from"
            />
            <Input
              type="date"
              value={toAt}
              onChange={(e) => setToAt(e.target.value)}
              className="h-8 text-sm"
              title="Recorded to"
            />
          </div>

          <div className="mt-2 flex gap-2">
            <Button size="sm" onClick={handleApply} disabled={loading}>
              Apply Filters
            </Button>
            <Button size="sm" variant="ghost" onClick={handleClear} disabled={loading} className="gap-1">
              <X className="h-3.5 w-3.5" /> Clear
            </Button>
            <span className="ml-auto text-xs text-muted-foreground self-center">
              {total.toLocaleString()} snapshot{total !== 1 ? 's' : ''}
            </span>
          </div>
        </CardHeader>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Postcode</TableHead>
                <TableHead className="text-xs">Beds</TableHead>
                <TableHead className="text-xs">Current Rent</TableHead>
                <TableHead className="text-xs">Median</TableHead>
                <TableHead className="text-xs">Min</TableHead>
                <TableHead className="text-xs">Max</TableHead>
                <TableHead className="text-xs">% of Median</TableHead>
                <TableHead className="text-xs">Listings</TableHead>
                <TableHead className="text-xs">Source</TableHead>
                <TableHead className="text-xs">Trigger</TableHead>
                <TableHead className="text-xs">Recorded</TableHead>
                <TableHead className="text-xs" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} className="py-12 text-center">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin text-muted-foreground" />
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={12} className="py-10 text-center text-sm text-muted-foreground">
                    No snapshots found. Adjust your filters or wait for the monthly job to run.
                  </TableCell>
                </TableRow>
              )}
              {items.map((item, idx) => (
                <TableRow key={item.property_id + (item.recorded_at || idx)} className="hover:bg-muted/40">
                  <TableCell className="text-sm font-medium">{item.postcode || '—'}</TableCell>
                  <TableCell className="text-sm">
                    {item.bedrooms_requested != null && item.bedrooms_requested !== item.bedrooms
                      ? (
                        <span className="text-amber-600 font-medium" title={`Requested ${item.bedrooms_requested} bed — insufficient data, showed ${item.bedrooms} bed`}>
                          {item.bedrooms_requested}({item.bedrooms ?? '?'})
                        </span>
                      )
                      : (item.bedrooms ?? '—')}
                  </TableCell>
                  <TableCell className="text-sm">{fmtGBP(item.current_rent)}</TableCell>
                  <TableCell className="text-sm font-semibold">{fmtGBP(item.median_rent)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{fmtGBP(item.min_rent)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{fmtGBP(item.max_rent)}</TableCell>
                  <TableCell>{pctBadge(item.current_rent_pct_of_median)}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">{item.listings_count ?? '—'}</TableCell>
                  <TableCell>{sourceBadge(item.source)}</TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {TRIGGER_LABELS[item.trigger] || item.trigger || '—'}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                    {fmtDate(item.recorded_at)}
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      title="View trend"
                      onClick={() =>
                        setTrendProp({
                          property_id: item.property_id,
                          postcode: item.postcode,
                          bedrooms: item.bedrooms,
                          bedrooms_requested: item.bedrooms_requested,
                        })
                      }
                    >
                      <LineChartIcon className="h-3.5 w-3.5 text-muted-foreground" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-4 py-3">
            <p className="text-xs text-muted-foreground">
              Page {page} of {totalPages} · {total.toLocaleString()} total
            </p>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                disabled={page <= 1 || loading}
                onClick={() => fetchHistory(page - 1)}
              >
                <ChevronLeft className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-7 w-7"
                disabled={page >= totalPages || loading}
                onClick={() => fetchHistory(page + 1)}
              >
                <ChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* trend dialog */}
      <TrendDialog
        open={!!trendProp}
        onClose={() => setTrendProp(null)}
        propertyId={trendProp?.property_id}
        postcode={trendProp?.postcode}
        bedrooms={trendProp?.bedrooms}
        bedroomsRequested={trendProp?.bedrooms_requested}
      />
    </div>
  );
}
